import { BILLING_PLANS, type BILLING_INTERVALS } from './index';

export type BillingPlan = (typeof BILLING_PLANS)[number];
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export type BillingPlanDetails = {
  name: string;
  /** Monthly price in euro cents, before the annual discount and excluding VAT. Display only: Polar's price is what is charged. */
  monthlyPrice: number;
  /** Sessions allowed per monthly window, pooled across the account's websites. Null for unlimited. */
  sessionsPerMonth: number | null;
  /** AI credits granted per monthly window. They do not carry over. */
  aiCreditsPerMonth: number;
};

/**
 * The account plans (decided 2026-10-05; see the Linear project "Billing: CC
 * billing"). Every plan includes unlimited websites, users and experiments and
 * every standard feature; they differ only in sessions and AI credits.
 *
 * Prices here are for showing the customer. What is charged is the price on the
 * Polar product each plan maps to (POLAR_PRODUCTS), and the two must be kept
 * equal by hand when either changes.
 */
export const BILLING_PLAN_CATALOGUE: Record<BillingPlan, BillingPlanDetails> = {
  start: { name: 'Start', monthlyPrice: 39_000, sessionsPerMonth: 100_000, aiCreditsPerMonth: 2_000 },
  grow: { name: 'Grow', monthlyPrice: 59_000, sessionsPerMonth: 250_000, aiCreditsPerMonth: 3_000 },
  scale: { name: 'Scale', monthlyPrice: 99_000, sessionsPerMonth: 600_000, aiCreditsPerMonth: 5_000 },
  business: { name: 'Business', monthlyPrice: 149_000, sessionsPerMonth: 1_100_000, aiCreditsPerMonth: 7_500 },
  unlimited: { name: 'Unlimited', monthlyPrice: 249_000, sessionsPerMonth: null, aiCreditsPerMonth: 12_500 },
};

/**
 * The agency fee, per month in euro cents. A product of its own (monthly and annual),
 * bought once by the agency and independent of its clients: it works with none, and
 * each client has a plan of its own on top.
 */
export const AGENCY_FEE_MONTHLY = 29_900;

/** The free trial on a first plan or agency fee, in days. Polar products carry it, and checkout asks for it. */
export const BILLING_TRIAL_DAYS = 12;

/** Annual plans cost this much less than twelve months. */
export const ANNUAL_DISCOUNT_PERCENT = 20;

export const AI_CREDIT_PACKS = ['credits_2500', 'credits_5000', 'credits_10000'] as const;
export type AiCreditPack = (typeof AI_CREDIT_PACKS)[number];

/** One-time AI credit purchases, price in euro cents. Purchased credits stay valid for PURCHASED_CREDITS_VALID_MONTHS. */
export const AI_CREDIT_PACK_CATALOGUE: Record<AiCreditPack, { price: number; credits: number }> = {
  credits_2500: { price: 2_500, credits: 2_500 },
  credits_5000: { price: 5_000, credits: 5_000 },
  credits_10000: { price: 10_000, credits: 10_000 },
};

export const PURCHASED_CREDITS_VALID_MONTHS = 12;

const periodPrice = ({ monthly, interval }: { monthly: number; interval: BillingInterval }): number =>
  interval === 'month' ? monthly : Math.round((monthly * 12 * (100 - ANNUAL_DISCOUNT_PERCENT)) / 100);

type PlanPriceParams = { plan: BillingPlan; interval: BillingInterval };

/**
 * What a plan costs per billing period, in euro cents, as shown to the
 * customer: twelve months less the annual discount for a yearly plan.
 */
export function planPeriodPrice({ plan, interval }: PlanPriceParams): number {
  return periodPrice({ monthly: BILLING_PLAN_CATALOGUE[plan].monthlyPrice, interval });
}

/** What the agency fee costs per billing period, in euro cents, with the same annual discount. */
export function agencyFeePeriodPrice(interval: BillingInterval): number {
  return periodPrice({ monthly: AGENCY_FEE_MONTHLY, interval });
}

/** The plan one step up, or null from the top. The automatic upgrade path. */
export function nextPlanUp(plan: BillingPlan): BillingPlan | null {
  const index = BILLING_PLANS.indexOf(plan);
  return BILLING_PLANS[index + 1] ?? null;
}

export type PlanChoice = { plan: BillingPlan; interval: BillingInterval };

export type PlanChangeDirection = 'upgrade' | 'downgrade' | 'none';

/**
 * Whether moving from one plan to another is an upgrade (applied now) or a
 * downgrade (applied at the next period). The plan decides first; between two
 * intervals of the same plan, annual is the upgrade, since it is the bigger
 * commitment and is paid up front.
 *
 * A higher plan on a shorter interval (Grow annual to Scale monthly) is still
 * an upgrade: the customer asked for more sessions, and waiting out an annual
 * period for them could take up to a year.
 *
 * Shared by the server (plan-change.ts, which bills by it) and the plan
 * picker, so a button never says something the server will not do.
 */
export function planChangeDirection(from: PlanChoice, to: PlanChoice): PlanChangeDirection {
  const fromRank = BILLING_PLANS.indexOf(from.plan);
  const toRank = BILLING_PLANS.indexOf(to.plan);
  if (toRank !== fromRank) return toRank > fromRank ? 'upgrade' : 'downgrade';
  if (from.interval === to.interval) return 'none';
  return to.interval === 'year' ? 'upgrade' : 'downgrade';
}

/** Share of the session allowance at which the first notice is sent, and at which the topbar meter turns yellow. */
export const ALLOWANCE_WARNING_SHARE = 0.8;
